import { createClient } from '@supabase/supabase-js';
import { createWrappedFetchForSupabaseDebug } from './supabaseFetchDebug';

const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').trim();
const supabaseAnonKey = (process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();

const IS_DEV_BUILD = process.env.NODE_ENV === 'development';

/** HMR / double-eval must not create a second GoTrueClient in the same tab. */
const GLOBAL_SINGLETON_KEY = '__devello_supabase_browser_client__';

function createBrowserClient() {
  if (!supabaseUrl || !supabaseAnonKey) return null;
  const browserStorage = typeof window !== 'undefined' ? window.localStorage : undefined;
  const baseFetch = typeof fetch !== 'undefined' && typeof window !== 'undefined' ? fetch.bind(window) : fetch;

  /** Exact createClient options (audit): URL + anon key + auth block + dev-only global.fetch wrapper. */
  return createClient(supabaseUrl, supabaseAnonKey, {
    ...(IS_DEV_BUILD
      ? {
          global: {
            fetch: createWrappedFetchForSupabaseDebug(baseFetch),
          },
        }
      : {}),
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      ...(browserStorage ? { storage: browserStorage } : {}),
    },
  });
}

/**
 * Single browser Supabase client. Reuses a window property in dev so Fast Refresh does not
 * instantiate multiple GoTrue clients (avoids "Multiple GoTrueClient instances" hangs).
 *
 * **HMR / stale options:** If `createBrowserClient` options change (e.g. fetch wrapper, auth.lock),
 * Fast Refresh keeps the **existing** instance on `window[GLOBAL_SINGLETON_KEY]` until a **full page
 * reload**. After editing this file, hard-reload to pick up new `createClient` options.
 */
function getSingletonBrowserClient() {
  const g = typeof window !== 'undefined' ? window : undefined;
  if (g && g[GLOBAL_SINGLETON_KEY]) {
    return g[GLOBAL_SINGLETON_KEY];
  }
  const client = createBrowserClient();
  if (g && client) {
    g[GLOBAL_SINGLETON_KEY] = client;
  }
  return client;
}

/** Single browser client; HMR reuses `window.__devello_supabase_browser_client__`. */
export const supabase = getSingletonBrowserClient();
